import clsx from 'clsx';
import { NavLink } from 'react-router-dom';
import type { HomeLayout } from '../../lib/home-schema.js';
import { useAuth } from '../../lib/auth.js';
import { filterByAllowed } from '@sfp/shared/permissions';

interface SidebarProps {
  layout: HomeLayout;
  role: string | null;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export function Sidebar({ layout, role, collapsed, onToggleCollapse }: SidebarProps) {
  const { user, signOut, allowedSidebarItemIds } = useAuth();
  const isAdmin = role === 'admin';
  const logoUrl = layout.brand.logoUrl;

  return (
    <aside
      className={clsx(
        'group flex shrink-0 flex-col bg-[color:var(--sfp-sidebar)] text-stone-200 transition-[width] duration-200',
        collapsed ? 'w-14' : 'w-60',
      )}
    >
      <div className="flex items-center justify-between gap-2 px-3 pb-3 pt-4">
        <div className={clsx('flex items-center gap-2.5', collapsed && 'mx-auto')}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={layout.brand.name}
              className="h-8 w-8 rounded-md object-cover"
            />
          ) : (
            <div className="grid h-8 w-8 place-items-center rounded-md bg-[color:var(--sfp-primary)] text-base font-bold text-[color:var(--sfp-sidebar)]">
              {layout.brand.markGlyph ?? '◆'}
            </div>
          )}
          {!collapsed && (
            <div>
              <div className="text-sm font-semibold text-stone-50">{layout.brand.name}</div>
              {layout.brand.tagline && (
                <div className="text-[10px] uppercase tracking-wider text-stone-500">
                  {layout.brand.tagline}
                </div>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={clsx(
            'shrink-0 rounded text-stone-400 hover:bg-[color:var(--sfp-sidebar-hover)] hover:text-white',
            collapsed ? 'absolute right-1 top-4 px-1.5 py-1 text-xs' : 'px-1.5 py-1',
          )}
        >
          {collapsed ? '›' : '‹'}
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        {layout.sidebar.map((section) => (
          <div key={section.id} className="mb-2">
            {section.title && !collapsed && (
              <div className="px-3 pb-1 pt-3 text-[10px] uppercase tracking-wider text-stone-500">
                {section.title}
              </div>
            )}
            {filterByAllowed(
              section.items.filter((it) => !it.roles || (role && it.roles.includes(role))),
              isAdmin ? [] : allowedSidebarItemIds,
            ).map((it) => (
              <NavLink
                key={it.id}
                to={it.href}
                end={it.href === '/'}
                title={collapsed ? it.label : undefined}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm',
                    collapsed && 'justify-center px-0',
                    isActive
                      ? 'bg-[color:var(--sfp-sidebar-active)] text-white'
                      : 'text-stone-300 hover:bg-[color:var(--sfp-sidebar-hover)] hover:text-white',
                  )
                }
              >
                <span className="w-4 text-center text-stone-400">{it.icon ?? '•'}</span>
                {!collapsed && <span>{it.label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="border-t border-[color:var(--sfp-sidebar-hover)] px-3 py-3">
        {collapsed ? (
          <button
            type="button"
            onClick={() => void signOut()}
            title="Sign out"
            className="mx-auto block rounded text-stone-400 hover:text-white"
          >
            ⎋
          </button>
        ) : (
          <>
            <div className="truncate text-xs text-stone-300">{user?.email}</div>
            <div className="mt-0.5 text-[10px] uppercase tracking-wider text-stone-500">
              {role ?? 'no role'}
            </div>
            <button
              type="button"
              className="mt-2 text-[11px] text-stone-400 hover:text-white"
              onClick={() => void signOut()}
            >
              Sign out
            </button>
          </>
        )}
      </div>
    </aside>
  );
}
